import { z } from 'zod';

import {
  CHART_PERIOD_RELATIVE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
} from '@/entities/chart/model/constants';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import {
  createDefaultTemplateDateTerm,
  getTemplatePeriodicTermMaxValue,
} from '../../model/date-term';
import type {
  ProductSentimentFormValues,
  TemplateFormConfig,
} from '../../model/types';

// --- FilterKeyValue 스키마 (StructuredFilter 호환) ---

const filterKeyValueSchema = z.object({
  filterKey: z.union([z.string(), z.number()]),
  filterName: z.string(),
  paths: z.array(z.string()).optional(),
});

const periodicTermUnitSchema = z.union([
  z.literal(CHART_TERM_UNIT.DAY),
  z.literal(CHART_TERM_UNIT.WEEK),
  z.literal(CHART_TERM_UNIT.MONTH),
  z.literal(CHART_TERM_UNIT.QUARTER),
]);

const periodicTermRelativeSchema = z.union([
  z.literal(CHART_PERIOD_RELATIVE.INCLUDE),
  z.literal(CHART_PERIOD_RELATIVE.EXCLUDE),
]);

const fixedDateTermSchema = z.object({
  type: z.literal(CHART_TERM_TYPE.FIXED),
  startDate: z.string().min(1, { message: 'Please select a start date' }),
  endDate: z.string().min(1, { message: 'Please select an end date' }),
});

const periodicDateTermSchema = z
  .object({
    type: z.literal(CHART_TERM_TYPE.PERIODIC),
    unit: periodicTermUnitSchema,
    relative: periodicTermRelativeSchema,
    value: z.number().int().min(1, { message: 'Please enter at least 1' }),
  })
  .superRefine((value, ctx) => {
    const max = getTemplatePeriodicTermMaxValue(value.unit);
    if (value.value > max) {
      const unitLabelMap: Record<typeof value.unit, string> = {
        [CHART_TERM_UNIT.DAY]: 'Day',
        [CHART_TERM_UNIT.WEEK]: 'Week',
        [CHART_TERM_UNIT.MONTH]: 'Month',
        [CHART_TERM_UNIT.QUARTER]: 'Quarter',
      };
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: `Max range on ${unitLabelMap[value.unit]} is ${max}.`,
      });
    }
  });

const dateTermSchema = z.discriminatedUnion('type', [
  fixedDateTermSchema,
  periodicDateTermSchema,
]);

// --- Template Form Schema ---

export const productSentimentFormSchema = z.object({
  title: z.string().min(1, { message: 'Please enter a dashboard title' }),
  dateTerm: dateTermSchema,
  productFilter: z
    .array(filterKeyValueSchema)
    .min(1, { message: 'Please select at least one product' }),
  topicFilter: z.array(filterKeyValueSchema),
});

// --- FilterKeyValue[] → StructuredFilter 변환 ---

const buildFilterFromKeyValues = (
  columnKey: string,
  values: FilterKeyValue[],
): StructuredFilter => {
  if (values.length === 0) return {};
  return {
    [columnKey]: [
      {
        op: STRUCTURED_FILTER_OPERATOR.OR,
        values: values.map((v) => v.filterKey),
      },
    ],
  };
};

// --- Form Config ---

export const productSentimentFormConfig: TemplateFormConfig<ProductSentimentFormValues> = {
  schema: productSentimentFormSchema,
  defaultValues: {
    title: '',
    dateTerm: createDefaultTemplateDateTerm(),
    productFilter: [],
    topicFilter: [],
  },
  fieldMapping: {
    product_id: (formValues) =>
      buildFilterFromKeyValues('product_id', formValues.productFilter),
    taxonomy_topic_id: (formValues) =>
      buildFilterFromKeyValues('taxonomy_topic_id', formValues.topicFilter),
  },
};
